// LiveKit meeting — Meet-like room (grid, screen share, controls, chat) via
// @livekit/components-react. Active when REACT_APP_LIVEKIT_URL is set; otherwise
// Dispatch falls back to the Daily room. Token comes from /api/livekit-token.
//
// Flow: PreJoin lobby (camera/mic preview + device pick) → Join → live room.
import React, { useEffect, useState, useMemo, useRef } from 'react';
import '@livekit/components-styles';
import { LiveKitRoom, VideoConference, PreJoin, useConnectionState } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import CallExtras from './CallExtras';
import { supabase } from '../supabaseClient';

const LIVEKIT_URL = process.env.REACT_APP_LIVEKIT_URL || '';

// Scoped overrides: brand-tint the LiveKit theme (teal accent, Sanoire dark) and
// make the pre-join + conference fill the panel. Mobile rules keep the pre-join
// scrollable so the Join button is always reachable.
const FILL_CSS = `
@keyframes sanoirePulseDot { 0%,100%{opacity:1} 50%{opacity:.35} }
.sanoire-lk {
  --lk-accent-bg:#314F5C; --lk-accent-fg:#E0F5FC;
  --lk-accent2:#3c5d6b;
  --lk-bg:#0b1620; --lk-bg2:#11202c; --lk-fg:#eef5f9; --lk-fg2:#b9cdd8;
  --lk-border-color:rgba(255,255,255,0.08);
  --lk-control-bg:rgba(255,255,255,0.06);
  --lk-control-hover-bg:rgba(255,255,255,0.12);
  --lk-control-active-bg:#314F5C; --lk-control-active-hover-bg:#3c5d6b;
}
.sanoire-lk, .sanoire-lk .lk-room-container { width:100%; height:100%; }
.sanoire-lk .lk-video-conference { height:100%; }
.sanoire-lk .lk-prejoin {
  flex:1; min-height:0; width:100%; max-width:none; padding:8px 24px 24px;
  display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px;
  background:transparent; overflow-y:auto;
}
.sanoire-lk .lk-prejoin .lk-video-container,
.sanoire-lk .lk-prejoin video {
  width:100%; max-width:680px; flex:1; min-height:240px; max-height:46vh;
  border-radius:16px; object-fit:cover; background:#0b1620;
  box-shadow:0 10px 34px rgba(0,0,0,0.4);
}
.sanoire-lk .lk-prejoin .lk-button-group,
.sanoire-lk .lk-prejoin .lk-button { font-size:14px; border-radius:10px; }
.sanoire-lk .lk-prejoin .lk-username-container { width:100%; max-width:680px; }
.sanoire-lk .lk-prejoin .lk-username-container input {
  font-size:14px; border-radius:11px; padding:12px 14px;
  background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); color:#eef5f9;
}
.sanoire-lk .lk-prejoin .lk-join-button,
.sanoire-lk .lk-prejoin button[type="submit"] {
  width:100%; max-width:680px; padding:14px; border-radius:12px;
  font-size:15px; font-weight:700; letter-spacing:-.01em;
  background:#314F5C; color:#E0F5FC; box-shadow:0 4px 16px rgba(49,79,92,0.4);
}
.sanoire-lk .lk-prejoin .lk-join-button:hover { background:#3c5d6b; }
.sanoire-lk .lk-video-conference { position: relative; overflow: hidden; display: flex; flex-direction: row; }
.sanoire-lk .lk-video-conference > *:not(.lk-chat) { flex: 1 1 auto; min-width: 0; }
.sanoire-lk .lk-chat {
  position: static; inset: auto; width: auto; height: auto;
  flex: 0 0 clamp(280px, 30%, 360px); align-self: stretch; min-width: 0;
  background: #0f1f2b; border-left: 1px solid rgba(255,255,255,0.1);
  display: flex; flex-direction: column;
}
.sanoire-lk .lk-chat-header {
  padding: 14px 16px; font-size: 13.5px; font-weight: 700; letter-spacing: -.01em;
  color: #eef5f9; border-bottom: 1px solid rgba(255,255,255,0.08);
  display: flex; align-items: center; justify-content: space-between;
}
.sanoire-lk .lk-chat-messages, .sanoire-lk .lk-chat ul.lk-list {
  flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 10px; list-style: none; margin: 0;
}
.sanoire-lk .lk-chat-entry { font-size: 13px; line-height: 1.5; color: #d9e6ee; }
.sanoire-lk .lk-chat-entry .lk-meta-data, .sanoire-lk .lk-chat-entry .lk-participant-name { color: #AFE1F6; font-weight: 700; font-size: 11.5px; }
.sanoire-lk .lk-chat-entry .lk-message-body { background: rgba(255,255,255,0.05); border-radius: 10px; padding: 8px 11px; display: inline-block; margin-top: 3px; }
.sanoire-lk .lk-chat-form { padding: 12px; border-top: 1px solid rgba(255,255,255,0.08); gap: 8px; align-items: center; background: #0f1f2b; }
.sanoire-lk .lk-chat-form-input {
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px; color: #eef5f9; padding: 10px 12px; font-size: 13px; flex: 1; outline: none;
}
.sanoire-lk .lk-chat-form-input::placeholder { color: #6f8b9b; }
.sanoire-lk .lk-chat-form-button {
  background: #314F5C; color: #E0F5FC; border-radius: 10px; border: none; padding: 0 14px; height: 38px; font-weight: 600; cursor: pointer;
}
.sanoire-lk .lk-chat-form-button:hover { background: #3c5d6b; }
.sanoire-lk .lk-chat .lk-close-button { color: #9fb6c4; background: transparent; }
@media (max-width: 767px) {
  .sanoire-lk .lk-control-bar { margin-bottom: 64px; }
  .sanoire-lk .lk-prejoin {
    justify-content:flex-start; padding:6px 12px 18px; gap:11px;
  }
  .sanoire-lk .lk-prejoin .lk-video-container,
  .sanoire-lk .lk-prejoin video {
    max-width:100%; min-height:150px; max-height:30vh; flex:0 0 auto;
  }
  .sanoire-lk .lk-prejoin .lk-button-group,
  .sanoire-lk .lk-prejoin .lk-button { font-size:13px; }
  .sanoire-lk .lk-prejoin .lk-button-group { flex-wrap:wrap; }
  .sanoire-lk .lk-prejoin .lk-username-container input { font-size:13px; padding:11px 12px; }
  .sanoire-lk .lk-prejoin .lk-join-button,
  .sanoire-lk .lk-prejoin button[type="submit"] { padding:13px; font-size:14px; }
  .sanoire-lk .lk-chat { position: absolute; left: 0; right: 0; bottom: 0; top: auto; height: 56%; max-height: 56%; width: 100%; max-width: none; z-index: 40; flex: none; border-left: none; border-top: 1px solid rgba(255,255,255,0.14); border-radius: 16px 16px 0 0; box-shadow: 0 -12px 34px rgba(0,0,0,0.5); }
}
`;


function ConnStatus() {
  const state = useConnectionState();
  if (state === ConnectionState.Connected) return null;
  const map = {};
  map[ConnectionState.Connecting] = { t: 'Connecting\u2026', c: '#AFE1F6' };
  map[ConnectionState.Reconnecting] = { t: 'Reconnecting\u2026', c: '#E6B400' };
  map[ConnectionState.SignalReconnecting] = { t: 'Reconnecting\u2026', c: '#E6B400' };
  map[ConnectionState.Disconnected] = { t: 'Connection lost \u2014 leave and rejoin if this persists', c: '#ef5350' };
  const s = map[state];
  if (!s) return null;
  return (
    <div style={{ position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 50, background: 'rgba(11,22,32,0.96)', color: s.c, border: '1px solid rgba(255,255,255,0.14)', borderRadius: 999, padding: '7px 16px', fontFamily: 'system-ui,-apple-system,sans-serif', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.4)' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.c, animation: 'sanoirePulseDot 1.2s ease-in-out infinite' }} />{s.t}
    </div>
  );
}

export default function LiveKitMeeting({ room, name, isMobile, onLeave, sessionId, me }) {
  const identity = useMemo(() => 'u-' + Math.random().toString(16).slice(2, 8), []);
  const [token, setToken] = useState(null);
  const [err, setErr] = useState(null);
  const [choices, setChoices] = useState(null); // set when the user clicks Join
  const [mCam, setMCam] = useState(true);
  const [mMic, setMMic] = useState(true);
  const previewRef = useRef(null);
  const [activeRoom, setActiveRoom] = useState(room);
  const switchingRef = useRef(false);
  const switchRoom = (r) => { try { switchingRef.current = true; } catch (_e) {} setActiveRoom(r); setTimeout(() => { try { switchingRef.current = false; } catch (_e) {} }, 4000); };

  useEffect(() => {
    if (!activeRoom) return;
    let cancelled = false;
    setToken(null); setErr(null);
    const qs = new URLSearchParams({ room: activeRoom, identity, name: name || identity }).toString();
    (async () => {
      const headers = {};
      try { const { data } = await supabase.auth.getSession(); const tk = data && data.session && data.session.access_token; if (tk) headers.Authorization = 'Bearer ' + tk; } catch (_e) {}
      try {
        const r = await fetch('/api/livekit-token?' + qs, { headers });
        const d = await r.json();
        if (cancelled) return;
        if (d && d.token) setToken(d.token); else setErr((d && d.error) || 'Could not get access token');
      } catch (e) { if (!cancelled) setErr((e && e.message) || 'Network error'); }
    })();
    return () => { cancelled = true; };
  }, [activeRoom, identity, name]);

  // Mobile pre-join camera preview
  useEffect(() => {
    if (!isMobile || choices) return;
    let stream = null, stopped = false;
    if (mCam) {
      navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })
        .then(st => { if (stopped) { st.getTracks().forEach(t => t.stop()); return; } stream = st; if (previewRef.current) previewRef.current.srcObject = st; })
        .catch(() => {});
    }
    return () => { stopped = true; if (stream) stream.getTracks().forEach(t => t.stop()); };
  }, [isMobile, choices, mCam]);

  // The container always fills its bounded parent (height:100%) so the inner
  // pre-join can scroll on small screens instead of clipping the Join button.
  const container = { flex: 1, minHeight: 0, height: '100%', width: '100%', display: 'flex', background: '#0b1620' };
  const msg = { ...container, alignItems: 'center', justifyContent: 'center', background: '#0b1b27', color: '#AFE1F6', fontSize: 13, padding: 24, textAlign: 'center', fontFamily: 'system-ui,-apple-system,sans-serif' };

  if (!LIVEKIT_URL) return <div style={msg}>LiveKit isn't configured yet — set REACT_APP_LIVEKIT_URL (and LIVEKIT_API_KEY / LIVEKIT_API_SECRET) on Vercel, then redeploy.</div>;
  if (err) return <div style={msg}>Couldn't start the meeting: {err}</div>;

  // 1) Pre-join lobby — heading + preview camera/mic, pick devices, then Join.
  if (!choices) {
    if (isMobile) {
      const FF = 'system-ui,-apple-system,sans-serif';
      const tgl = (on) => ({ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '13px 0', borderRadius: 14, border: '1px solid ' + (on ? 'transparent' : 'rgba(255,255,255,0.16)'), background: on ? '#314F5C' : 'rgba(255,255,255,0.06)', color: on ? '#E0F5FC' : '#9fb6c4', cursor: 'pointer', fontFamily: FF, fontSize: 13, fontWeight: 600 });
      return (
        <div data-lk-theme="default" className="sanoire-lk" style={{ ...container, flexDirection: 'column', justifyContent: 'flex-start', padding: 14 }}>
          <style>{FILL_CSS}</style>
          <div style={{ textAlign: 'center', flexShrink: 0, marginBottom: 12 }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#eef5f9', letterSpacing: '-.02em', fontFamily: FF }}>Ready to join?</div>
            <div style={{ fontSize: '.78rem', color: '#8fa9b8', marginTop: 4, fontFamily: FF }}>Set your camera and mic, then join.</div>
          </div>
          <div style={{ flex: 1, minHeight: 0, width: '100%', borderRadius: 16, overflow: 'hidden', background: '#0b1620', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
            {mCam
              ? <video ref={previewRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
              : <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#4a5e6b' }}><svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M16 16v1a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m4 0h4a2 2 0 0 1 2 2v3l4-3v9" /><line x1="1" y1="1" x2="23" y2="23" /></svg><span style={{ fontSize: 13, fontFamily: FF }}>Camera is off</span></div>}
          </div>
          <div style={{ display: 'flex', gap: 10, width: '100%', marginBottom: 12, flexShrink: 0 }}>
            <button onClick={() => setMCam(v => !v)} style={tgl(mCam)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{mCam ? <><path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" /></> : <><path d="M16 16v1a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m4 0h4a2 2 0 0 1 2 2v3l4-3v9" /><line x1="1" y1="1" x2="23" y2="23" /></>}</svg>
              {mCam ? 'Camera on' : 'Camera off'}
            </button>
            <button onClick={() => setMMic(v => !v)} style={tgl(mMic)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{mMic ? <><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /></> : <><line x1="1" y1="1" x2="23" y2="23" /><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" /><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" /><line x1="12" y1="19" x2="12" y2="23" /></>}</svg>
              {mMic ? 'Mic on' : 'Mic off'}
            </button>
          </div>
          <button onClick={() => setChoices({ videoEnabled: mCam, audioEnabled: mMic })} style={{ width: '100%', flexShrink: 0, padding: 15, borderRadius: 14, border: 'none', background: '#314F5C', color: '#E0F5FC', fontWeight: 700, fontSize: 16, cursor: 'pointer', fontFamily: FF, boxShadow: '0 4px 16px rgba(49,79,92,0.4)' }}>Join call</button>
        </div>
      );
    }
    return (
      <div data-lk-theme="default" className="sanoire-lk" style={{ ...container, flexDirection: 'column' }}>
        <style>{FILL_CSS}</style>
        <div style={{ textAlign: 'center', padding: '26px 24px 4px', flexShrink: 0 }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#eef5f9', letterSpacing: '-.02em', fontFamily: 'system-ui,-apple-system,sans-serif' }}>Ready to join?</div>
          <div style={{ fontSize: '.8rem', color: '#8fa9b8', marginTop: 4, fontFamily: 'system-ui,-apple-system,sans-serif' }}>Check your camera and microphone, then join the Team Room.</div>
        </div>
        <PreJoin
          defaults={{ username: name || 'Member', videoEnabled: true, audioEnabled: true }}
          onSubmit={(values) => setChoices(values)}
          onError={(e) => setErr((e && e.message) || 'Could not access your devices')}
          joinLabel="Join call"
        />
      </div>
    );
  }

  // 2) Token still loading after Join was pressed.
  if (!token) return <div style={msg}>Connecting to the room…</div>;

  // 3) Live room, honouring the device choices from the lobby.
  return (
    <div data-lk-theme="default" className="sanoire-lk" style={container}>
      <style>{FILL_CSS}</style>
      <LiveKitRoom key={activeRoom} serverUrl={LIVEKIT_URL} token={token} connect
        video={choices.videoEnabled ? (choices.videoDeviceId ? { deviceId: choices.videoDeviceId } : true) : false}
        audio={choices.audioEnabled ? (choices.audioDeviceId ? { deviceId: choices.audioDeviceId } : true) : false}
        onDisconnected={() => { if (switchingRef.current) return; onLeave && onLeave(); }} style={{ flex: 1, height: '100%', minWidth: 0, position: 'relative' }}>
        <VideoConference />
        <ConnStatus />
        <CallExtras roomName={activeRoom} mainRoom={room} activeRoom={activeRoom} onSwitchRoom={switchRoom} sessionId={sessionId} me={me} supabase={supabase} />
      </LiveKitRoom>
    </div>
  );
}
